"use client"

import { useEffect, useState } from "react"
import { Bell, LogOut, Menu, RefreshCw } from "lucide-react"
import HamburgerMenu from "../modals/HamburguerMenu"
import logo from "../imgs/Logo_Fcn.png"
import { smartCase } from "../utils/text"

const NAV_ITEMS = [
  { id: "desktop", label: "Aplicaciones" },
  { id: "calendar", label: "Calendario" },
  { id: "files", label: "Archivos" },
  { id: "help", label: "Ayuda y soporte" },
]

const getInitial = (user) =>
  (user?.empleado?.NombreEmpleado || user?.usuario || "U").trim().charAt(0).toUpperCase()

const Layout = ({
  user,
  children,
  activeTab,
  setActiveTab,
  onRefresh,
  loading,
  notifications = [],
  onShowNotifications,
  onShowProfile,
  onShowLogout,
  onShowHelpSupport,
  onShowFileLibrary,
}) => {
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  // La barra es transparente arriba y toma fondo y línea fina solo al bajar la página
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  // Al cambiar de sección la nueva vista empieza arriba, no a media página
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [activeTab])

  const unreadNotifications = notifications.filter((n) => !n.read).length
  const activeItem = NAV_ITEMS.find((item) => item.id === activeTab)
  const displayName = smartCase(user?.empleado?.NombreEmpleado || user?.usuario || "Usuario")

  return (
    <div className="min-h-screen bg-[#FBF8F4] font-sans text-left text-cafe-800">
      <div className="flex min-h-screen flex-col">
        {/* BARRA SUPERIOR: logo, navegación y acciones */}
        <header
          className={`sticky top-0 z-30 border-b transition-colors duration-300 ${
            scrolled ? "border-cafe-100/70 bg-[#FBF8F4]/90 backdrop-blur" : "border-transparent bg-transparent"
          }`}
        >
          <div className="flex h-16 items-center justify-between gap-6 px-4 sm:px-8 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:grid-rows-[4rem] lg:px-10">
            <div className="flex min-w-0 items-center gap-3 self-stretch">
              <button
                onClick={() => setShowHamburgerMenu(true)}
                aria-label="Abrir menú"
                className="lg:hidden -ml-1 rounded-lg p-2 text-cafe-700 hover:bg-cafe-100/50 transition-colors duration-200"
              >
                <Menu className="h-5 w-5" />
              </button>
              {/* Logo completo (ya incluye el nombre). Arriba de la página sobresale por debajo
                  de la barra; al bajar se encoge para caber en ella */}
              <img
                src={logo}
                alt="Logo Fondo Cafetero Nacional"
                className={`mt-2 w-auto flex-shrink-0 self-start transition-all duration-500 ease-out ${
                  scrolled ? "h-12" : "h-24 lg:h-32"
                }`}
              />
              {/* La navegación ya indica la sección activa; se conserva como título para lectores de pantalla */}
              <h1 className="sr-only">{activeItem?.label || "Portal"}</h1>
            </div>

            {/* Navegación principal - Solo escritorio */}
            <nav className="hidden items-stretch self-stretch lg:flex" aria-label="Navegación principal">
              {NAV_ITEMS.map(({ id, label }) => {
                const isActive = activeTab === id
                return (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    aria-current={isActive ? "page" : undefined}
                    className={`relative flex items-center px-4 font-display text-[15px] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-sol-400/70 ${
                      isActive ? "font-semibold text-cafe-800" : "font-medium text-neutral-300 hover:text-cafe-700"
                    }`}
                  >
                    {label}
                    {isActive && (
                      <span className="absolute inset-x-4 -bottom-px h-[2px] rounded-full bg-cafe-700" />
                    )}
                  </button>
                )
              })}
            </nav>

            <div className="flex flex-shrink-0 items-center gap-1 lg:justify-self-end">
              <button
                onClick={onRefresh}
                disabled={loading}
                title="Actualizar"
                aria-label="Actualizar"
                className="rounded-lg p-2.5 text-neutral-300 hover:bg-cafe-100/50 hover:text-cafe-700 transition-colors duration-200 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70"
              >
                <RefreshCw className={`h-[18px] w-[18px] ${loading ? "animate-spin" : ""}`} />
              </button>

              <button
                onClick={onShowNotifications}
                title="Notificaciones"
                aria-label="Notificaciones"
                className="relative rounded-lg p-2.5 text-neutral-300 hover:bg-cafe-100/50 hover:text-cafe-700 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70"
              >
                <Bell className="h-[18px] w-[18px]" />
                {unreadNotifications > 0 && (
                  <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-secondary-700 px-1 text-[10px] font-semibold leading-none text-white">
                    {unreadNotifications > 9 ? "9+" : unreadNotifications}
                  </span>
                )}
              </button>

              <button
                onClick={onShowLogout}
                title="Cerrar sesión"
                aria-label="Cerrar sesión"
                className="rounded-lg p-2.5 text-neutral-300 hover:bg-red-50 hover:text-red-700 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>

              {/* Perfil - Solo escritorio (en móvil está en el menú) */}
              <span aria-hidden="true" className="mx-2 hidden h-6 w-px bg-cafe-100 lg:block" />
              <button
                onClick={onShowProfile}
                title="Mi perfil"
                className="hidden items-center gap-2.5 rounded-lg py-1 pl-1 pr-2 text-left transition-colors duration-200 hover:bg-cafe-100/40 lg:flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70"
              >
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-cafe-200/60 text-sm font-semibold text-cafe-700">
                  {getInitial(user)}
                </span>
                <span className="hidden max-w-[16rem] truncate text-sm font-medium text-cafe-800 xl:block">
                  {displayName}
                </span>
              </button>
            </div>
          </div>
        </header>

        {/* CONTENIDO PRINCIPAL */}
        <main className="flex-1 px-4 py-6 sm:px-8 sm:py-8">
          {/* La key reinicia la animación de entrada en cada cambio de sección */}
          <div key={activeTab} className="portal-view mx-auto max-w-7xl">
            {children}
          </div>
        </main>

        <footer className="px-4 pb-6 pt-2 sm:px-8">
          <div className="mx-auto max-w-7xl">
            <div aria-hidden="true" className="h-px bg-gradient-to-r from-transparent via-cafe-200/70 to-transparent" />
            <p className="pt-5 text-center text-xs text-neutral-300">
              © {new Date().getFullYear()} Fondo Cafetero Nacional
            </p>
          </div>
        </footer>
      </div>

      {/* Menú móvil */}
      <HamburgerMenu
        show={showHamburgerMenu}
        onClose={() => setShowHamburgerMenu(false)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onShowNotifications={onShowNotifications}
        onShowFileLibrary={onShowFileLibrary}
        onShowHelpSupport={onShowHelpSupport}
        onShowProfile={onShowProfile}
        onShowLogout={onShowLogout}
        unreadNotifications={unreadNotifications}
        user={user}
      />
    </div>
  )
}

export default Layout
